import React, { useEffect, useState } from "react";
import { X, RotateCcw, History, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";

/**
 * SnapshotHistoryDrawer — right-side sliding drawer showing every auto-save
 * snapshot for a given kind ('profile' | 'funding'). Each row is expandable
 * to show diffs; each changed field can be reverted individually.
 */
export default function SnapshotHistoryDrawer({ open, onClose, kind, onReverted }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [reverting, setReverting] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/startup/snapshots?kind=${kind}&limit=30`);
      setItems(res.data.items || []);
    } catch (e) { toast.error("Couldn't load history."); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open, kind]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const revert = async (snap, field) => {
    setReverting(snap.snap_id + ":" + field);
    try {
      await api.post(`/startup/snapshots/${snap.snap_id}/revert-field`, { field });
      toast.success(`Reverted "${field}".`);
      onReverted?.(field);
      await load();
    } catch (e) { toast.error("Revert failed."); }
    finally { setReverting(null); }
  };

  const startEditNote = (snap) => {
    setEditingNoteId(snap.snap_id);
    setNoteDraft(snap.annotation || "");
  };

  const saveNote = async (snap) => {
    setSavingNote(true);
    try {
      await api.post(`/startup/snapshots/${snap.snap_id}/annotate`, { annotation: noteDraft });
      toast.success("Note saved.");
      setEditingNoteId(null);
      await load();
    } catch (e) { toast.error("Couldn't save note."); }
    finally { setSavingNote(false); }
  };

  if (!open) return null;

  const kindLabel = kind === "profile" ? "Company profile" : "Funding profile";

  return (
    <div className="fixed inset-0 z-[85]" data-testid="snapshot-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        <header className="px-6 py-4 border-b border-navy/10 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <History size={14} className="text-emerald-600" />
              <div className="overline text-emerald-700">Draft Snapshots</div>
            </div>
            <div className="font-heading text-lg text-ink mt-1">{kindLabel} history</div>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink"
            aria-label="Close history"
            data-testid="snapshot-drawer-close"
          ><X size={20} /></button>
        </header>

        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {loading && <div className="text-sm text-ink-muted">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="text-sm text-ink-muted p-6 text-center border border-dashed border-navy/15 rounded-lg">
              No snapshots yet. Every auto-save creates a checkpoint here — up to 40 kept.
            </div>
          )}
          {items.map((s) => {
            const isExpanded = expandedId === s.snap_id;
            return (
              <div
                key={s.snap_id}
                className={`rounded-lg border ${isExpanded ? "border-emerald-300 bg-emerald-50/30" : "border-navy/10 bg-white"}`}
                data-testid={`snapshot-item-${s.snap_id}`}
              >
                <button
                  onClick={() => setExpandedId(isExpanded ? null : s.snap_id)}
                  className="w-full text-left px-4 py-3 flex items-start justify-between hover:bg-navy/[0.02]"
                >
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-ink-muted">
                      {formatDateTime(s.created_at)}
                    </div>
                    <div className="text-sm text-ink mt-1">
                      {s.changed_count === 0
                        ? <span className="text-ink-muted">No diff vs current</span>
                        : <><span className="font-heading text-emerald-700">{s.changed_count}</span> field{s.changed_count === 1 ? "" : "s"} different</>}
                    </div>
                    {s.annotation && editingNoteId !== s.snap_id && (
                      <div className="mt-2 text-xs text-ink bg-amber-50/60 border border-amber-200 rounded p-2 italic" data-testid={`snapshot-note-${s.snap_id}`}>
                        {s.annotation}
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] text-ink-muted">{isExpanded ? "Hide" : "View diff"}</div>
                </button>

                {/* Diary note editor */}
                <div className="px-4 pb-3 -mt-1">
                  {editingNoteId === s.snap_id ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <textarea
                        rows={2}
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        placeholder="e.g. Raised ask to 5Cr after Meeting with X"
                        className="w-full text-xs p-2 border border-emerald-200 rounded bg-white text-ink resize-none focus:outline-none focus:ring-1 focus:ring-emerald-400"
                        data-testid={`snapshot-note-input-${s.snap_id}`}
                        maxLength={1000}
                        autoFocus
                      />
                      <div className="flex items-center gap-2 mt-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); saveNote(s); }}
                          disabled={savingNote}
                          className="text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid={`snapshot-note-save-${s.snap_id}`}
                        >
                          <Check size={11} /> {savingNote ? "Saving…" : "Save note"}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingNoteId(null); }}
                          className="text-[11px] text-ink-muted hover:text-ink"
                          data-testid={`snapshot-note-cancel-${s.snap_id}`}
                        >Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); startEditNote(s); }}
                      className="text-[11px] text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 font-mono uppercase tracking-widest"
                      data-testid={`snapshot-note-edit-${s.snap_id}`}
                    >
                      <Pencil size={10} /> {s.annotation ? "Edit note" : "Add diary note"}
                    </button>
                  )}
                </div>

                {isExpanded && s.changed_count > 0 && (
                  <div className="px-4 pb-4 space-y-2 border-t border-navy/[0.06]">
                    {(s.changed_fields || []).map((c) => {
                      const busy = reverting === s.snap_id + ":" + c.field;
                      return (
                        <div key={c.field} className="pt-2" data-testid={`snapshot-diff-${c.field}`}>
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-heading text-ink">{c.field}</div>
                            <button
                              onClick={() => revert(s, c.field)}
                              disabled={busy}
                              className="text-[11px] px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 inline-flex items-center gap-1"
                              data-testid={`snapshot-revert-${s.snap_id}-${c.field}`}
                            >
                              <RotateCcw size={11} /> {busy ? "Reverting…" : "Revert"}
                            </button>
                          </div>
                          <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
                            <div className="border border-navy/10 rounded p-2 bg-white">
                              <div className="text-[9px] font-mono uppercase tracking-widest text-ink-muted mb-1">Was</div>
                              <div className="text-ink-soft break-words">{String(c.was ?? "—").slice(0, 140)}</div>
                            </div>
                            <div className="border border-emerald-200 rounded p-2 bg-emerald-50">
                              <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-700 mb-1">Now</div>
                              <div className="text-ink-soft break-words">{String(c.current ?? "—").slice(0, 140)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </div>
  );
}
